/**
 * Filter content based on custom section filters
 * @param {Array} content - Array of content items
 * @param {Object} filters - Filter criteria
 * @returns {Array} - Filtered content array
 */
export const filterContent = (content, filters) => {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return [];
  }

  if (!filters) {
    return content;
  }

  return content.filter(item => {
    // Filter by type (movie, tv, tv_channel)
    if (filters.types && filters.types.length > 0) {
      if (!filters.types.includes(item.type)) {
        return false;
      }
    }

    // Filter by source
    if (filters.sources && filters.sources.length > 0) {
      if (!item.source || !filters.sources.includes(item.source)) {
        return false;
      }
    }

    // Filter by genres
    if (filters.genres && filters.genres.length > 0) {
      if (!item.genre || !Array.isArray(item.genre)) {
        return false;
      }
      // Item must have at least one of the selected genres
      const hasMatchingGenre = filters.genres.some(filterGenre =>
        item.genre.some(itemGenre =>
          itemGenre.toLowerCase() === filterGenre.toLowerCase()
        )
      );
      if (!hasMatchingGenre) {
        return false;
      }
    }

    // Filter by year range
    if (filters.yearRange) {
      const { min, max } = filters.yearRange;
      if (item.year) {
        if (item.year < min || item.year > max) {
          return false;
        }
      }
    }

    // Filter by minimum rating
    if (filters.minRating && filters.minRating > 0) {
      const rating = item.imdbRating || item.tmdbRating || 0;
      if (rating < filters.minRating) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Get a summary of filter criteria for display
 * @param {Object} filters - Filter criteria
 * @returns {String} - Human-readable filter summary
 */
export const getFilterSummary = (filters) => {
  if (!filters) return '';

  const parts = [];

  if (filters.types && filters.types.length > 0) {
    const typeLabels = {
      movie: 'Movies',
      tv: 'TV Shows',
      tv_channel: 'Live TV'
    };
    parts.push(filters.types.map(t => typeLabels[t] || t).join(', '));
  }

  if (filters.genres && filters.genres.length > 0) {
    parts.push(filters.genres.join(', '));
  }

  if (filters.yearRange && (filters.yearRange.min !== 1900 || filters.yearRange.max !== new Date().getFullYear())) {
    parts.push(`${filters.yearRange.min}-${filters.yearRange.max}`);
  }

  if (filters.minRating && filters.minRating > 0) {
    parts.push(`${filters.minRating}+ rating`);
  }

  if (filters.sources && filters.sources.length > 0) {
    parts.push(`from ${filters.sources.join(', ')}`);
  }

  return parts.join(' • ');
};

/**
 * Parse runtime string to minutes for sorting
 * @param {String} runtime - Runtime string like "119 min" or "2h 15min"
 * @returns {Number} - Runtime in minutes
 */
const parseRuntime = (runtime) => {
  if (!runtime || typeof runtime !== 'string') return 0;
  
  // Handle "119 min" format
  const minutesMatch = runtime.match(/(\d+)\s*min/i);
  if (minutesMatch) {
    return parseInt(minutesMatch[1]);
  }
  
  // Handle "2h 15min" format
  const hoursMinutesMatch = runtime.match(/(\d+)h\s*(\d+)?min/i);
  if (hoursMinutesMatch) {
    const hours = parseInt(hoursMinutesMatch[1]) || 0;
    const minutes = parseInt(hoursMinutesMatch[2]) || 0;
    return hours * 60 + minutes;
  }
  
  return 0;
};

/**
 * Sort content based on specified criteria
 * @param {Array} content - Array of content items
 * @param {String} sortBy - Sort criteria (rating, year, runtime, title, popularity)
 * @param {String} sortOrder - Sort direction (desc, asc)
 * @returns {Array} - Sorted content array
 */
export const sortContent = (content, sortBy, sortOrder = 'desc') => {
  if (!content || !Array.isArray(content)) return [];
  if (!sortBy || sortBy === 'default') return content;
  
  const sortedContent = [...content];
  
  switch (sortBy) {
    case 'rating':
      return sortedContent.sort((a, b) => {
        const ratingA = a.imdbRating || a.tmdbRating || a.ratings?.imdbRating || a.ratings?.tmdbRating || 0;
        const ratingB = b.imdbRating || b.tmdbRating || b.ratings?.imdbRating || b.ratings?.tmdbRating || 0;
        return sortOrder === 'desc' ? ratingB - ratingA : ratingA - ratingB;
      });
      
    case 'year':
      return sortedContent.sort((a, b) => {
        const yearA = a.year || a.metadata?.year || 0;
        const yearB = b.year || b.metadata?.year || 0;
        return sortOrder === 'desc' ? yearB - yearA : yearA - yearB;
      });
      
    case 'runtime':
      return sortedContent.sort((a, b) => {
        const runtimeA = parseRuntime(a.runtime || a.metadata?.runtime);
        const runtimeB = parseRuntime(b.runtime || b.metadata?.runtime);
        return sortOrder === 'desc' ? runtimeB - runtimeA : runtimeA - runtimeB;
      });
      
    case 'title':
      return sortedContent.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return sortOrder === 'desc' ? titleB.localeCompare(titleA) : titleA.localeCompare(titleB);
      });
      
    case 'popularity':
      return sortedContent.sort((a, b) => {
        const popA = a.popularity || a.metadata?.popularity || 0;
        const popB = b.popularity || b.metadata?.popularity || 0;
        return sortOrder === 'desc' ? popB - popA : popA - popB;
      });
      
    default:
      return sortedContent;
  }
};

/**
 * Check if a movie has good quality metadata
 * @param {Object} movie - Movie object to assess
 * @returns {Boolean} - True if metadata is considered high quality
 */
export const hasGoodMetadata = (movie) => {
  if (!movie) return false;

  // Check for valid genres
  const hasGenres = (movie.genre && Array.isArray(movie.genre) && movie.genre.length > 0) ||
                   (movie.metadata?.genres && Array.isArray(movie.metadata.genres) && movie.metadata.genres.length > 0);

  // Enhanced rating validation to detect fake ratings
  const getRatingValue = () => {
    return movie.imdbRating || movie.tmdbRating || movie.ratings?.imdbRating || movie.ratings?.tmdbRating;
  };

  const hasValidRating = () => {
    const rating = getRatingValue();
    if (!rating || rating <= 0) return false;

    const year = movie.year || movie.metadata?.year;
    
    // MODERATE filtering - we now have batch refresh to fix bad data instead of hiding it
    
    // Only filter the most obviously fake content now
    
    // 1. Missing year - still a major red flag
    const hasValidYear = year && year >= 1900 && year <= new Date().getFullYear() + 1;
    if (!hasValidYear) {
      console.log(`[Metadata Filter] REJECTED - Missing/invalid year: "${movie.title}" (Year: ${year})`);
      return false;
    }
    
    // 2. Perfect 10.0 ratings (extremely rare)
    if (rating === 10.0) {
      console.log(`[Metadata Filter] REJECTED - Perfect 10.0 rating: "${movie.title}"`);
      return false;
    }
    
    // 3. Only filter impossibly high ratings
    if (rating > 9.9) {
      console.log(`[Metadata Filter] REJECTED - Impossibly high rating: "${movie.title}" (${rating})`);
      return false;
    }
    
    // 4. Only filter movies with no metadata at all
    const hasNoMetadata = (!movie.description || movie.description.length < 10) &&
                         (!movie.cast || movie.cast.length === 0 || movie.cast.includes('Unknown Actor')) &&
                         (!movie.genre || movie.genre.length === 0);
    
    if (hasNoMetadata && rating >= 8.0) {
      console.log(`[Metadata Filter] REJECTED - High rating with no metadata: "${movie.title}" (${rating})`);
      return false;
    }

    // Note: We're now much more lenient since we can fix suspicious ratings with batch refresh
    return true;
  };

  // Check for valid poster (not placeholder)
  const hasValidPoster = movie.thumbnailUrl && 
                        !movie.thumbnailUrl.includes('placeholder') &&
                        !movie.thumbnailUrl.includes('via.placeholder');

  // Check for runtime/duration
  const hasRuntime = (movie.duration && movie.duration !== 'Unknown' && movie.duration !== '') ||
                    (movie.metadata?.runtime && movie.metadata.runtime !== 'Unknown' && movie.metadata.runtime !== '');

  // Enhanced description check
  const hasDescription = (movie.description && 
                         movie.description !== 'Unknown' && 
                         movie.description !== '' &&
                         !movie.description.includes('is a ') && // Avoid generic generated descriptions
                         !movie.description.includes('is an ') &&
                         movie.description.length > 20) ||
                        (movie.metadata?.plot && 
                         movie.metadata.plot !== 'Unknown' && 
                         movie.metadata.plot !== '' &&
                         movie.metadata.plot.length > 20);

  // Enhanced cast check
  const hasCast = (movie.cast && Array.isArray(movie.cast) && movie.cast.length > 0 && !movie.cast.includes('Unknown Actor')) ||
                 (movie.metadata?.actors && Array.isArray(movie.metadata.actors) && movie.metadata.actors.length > 0 && !movie.metadata.actors.includes('Unknown Actor'));

  // A movie is considered to have "good metadata" if it has:
  // - Genres AND valid ratings AND (poster OR runtime OR description OR cast)
  const hasCore = hasGenres && hasValidRating();
  const hasSupplementary = hasValidPoster || hasRuntime || hasDescription || hasCast;

  return hasCore && hasSupplementary;
};

/**
 * Filter movies by metadata quality
 * @param {Array} content - Array of movies/content
 * @param {Boolean} hideIncompleteMetadata - Whether to filter out poor metadata
 * @returns {Array} - Filtered content array
 */
export const filterByMetadataQuality = (content, hideIncompleteMetadata = false) => {
  if (!content || !Array.isArray(content) || !hideIncompleteMetadata) {
    return content;
  }

  const originalCount = content.length;
  const filtered = content.filter(movie => hasGoodMetadata(movie));
  const rejectedCount = originalCount - filtered.length;
  
  if (rejectedCount > 0) {
    console.log(`[Metadata Filter] SUMMARY: ${rejectedCount} of ${originalCount} movies filtered out (${Math.round(rejectedCount/originalCount*100)}%)`);
    console.log(`[Metadata Filter] ${filtered.length} movies passed quality check`);
  }

  return filtered;
};

/**
 * Identify movies with suspicious/fake ratings that should be re-scraped
 * @param {Array} content - Array of movies
 * @returns {Array} - Array of movie IDs that need metadata refresh
 */
export const identifySuspiciousRatings = (content) => {
  if (!content || !Array.isArray(content)) {
    return [];
  }

  const suspiciousMovies = [];

  content.forEach(movie => {
    if (!movie || !movie.id) return;

    const rating = movie.imdbRating || movie.tmdbRating || movie.ratings?.imdbRating || movie.ratings?.tmdbRating;
    if (!rating || rating <= 0) return;

    const title = movie.title?.toLowerCase() || '';
    const year = movie.year || movie.metadata?.year;

    // Red flags for suspicious ratings
    let isSuspicious = false;
    let reasons = [];

    // 1. Missing or invalid year
    const hasValidYear = year && year >= 1900 && year <= new Date().getFullYear() + 1;
    if (!hasValidYear) {
      isSuspicious = true;
      reasons.push('Missing/invalid year');
    }

    // 2. Perfect scores or impossibly high ratings
    if (rating >= 9.8) {
      isSuspicious = true;
      reasons.push('Impossibly high rating');
    }

    // 3. Whole number ratings (very rare on real IMDb)
    if (rating % 1 === 0 && rating >= 7.0) {
      isSuspicious = true;
      reasons.push('Suspicious whole number rating');
    }

    // 4. Common fake rating values
    const commonFakeRatings = [7.0, 7.5, 8.0, 8.5, 9.0, 9.5];
    if (commonFakeRatings.includes(rating)) {
      isSuspicious = true;
      reasons.push('Common fake rating value');
    }

    // 5. High ratings on obvious sequels
    const suspiciousPatterns = [
      /\b(part|vol|volume)\s*[2-9]\d*\b/i,
      /\b(ii|iii|iv|v|vi|vii|viii|ix|x)\b(?!\s+(the|of|in|and))/i,
      /\s[2-9]\d*$/i,
      /\b(and his|and her)\s+\w+\s*[2-9]\d*\b/i
    ];
    
    if (rating >= 8.0 && suspiciousPatterns.some(pattern => pattern.test(title))) {
      isSuspicious = true;
      reasons.push('High-rated sequel pattern');
    }

    if (isSuspicious) {
      suspiciousMovies.push({
        id: movie.id,
        title: movie.title,
        rating: rating,
        year: year,
        reasons: reasons
      });
    }
  });

  console.log(`[Suspicious Ratings] Found ${suspiciousMovies.length} movies with suspicious ratings`);
  
  return suspiciousMovies;
};
